import React, { useState, useEffect, Fragment } from 'react';

import request from '@nineeng/request';

import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import ContentContainer from '../layout/ContentContainer.jsx';
import TextField from '../components/BasicMaterial.jsx';
import DatePicker from '../components/DatePicker.jsx';
import MultipleFileUpload from '../components/MultipleFileUpload.jsx';

import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';

import { format, parseISO } from 'date-fns';
import { normalizeText } from 'normalize-text';
import levenshtein from 'js-levenshtein';

import {
	getSatisfiedDocumentTypes,
	getTypesAcceptableForRequirements,
	flattenRequirementTypes,
	isRequirementGroupSatisfied,
	formatRequirementGroup,
	getUnsatisfiedRequirements,
} from '../util/safetyCertificates.js';

import { getTextDefaults } from './translations/safetyCertificatesTranslations';

// Human-readable names for certificate types
const CERTIFICATE_DISPLAY_NAMES = {
	'vca-user-vol': 'VCA Vol.',
	'vca-user-basis': 'VCA Basis',
	'ssc-user-vol': 'SSC Vol.',
	'ssc-user-basis': 'SSC Basis',
	'user-safety-certificate': 'User Safety Certificate',
};

// Page Components
const WelcomePage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>
				{text.title}
			</Typography>
			<Typography variant="body1">{text.intro}</Typography>
		</WelcomeScreen>
	);
};

const UserDetailsPage = ({ input, text, onInput, disabled, disableFirstName, disableLastName, disableDateOfBirth }) => {
	return (
		<ContentContainer>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>
				{text.userDetailsTitle}
			</Typography>
			<TextField
				label={text.lblFirstName}
				onChange={onInput}
				name="input.firstName"
				value={input.firstName || ''}
				disabled={disabled || disableFirstName}
				required
			/>
			<TextField
				label={text.lblLastName}
				onChange={onInput}
				name="input.lastName"
				value={input.lastName || ''}
				disabled={disabled || disableLastName}
				required
			/>
			<DatePicker
				label={text.lblDOB}
				onChange={onInput}
				name="input.dateOfBirth"
				value={input.dateOfBirth || ''}
				disabled={disabled || disableDateOfBirth}
				required
			/>
		</ContentContainer>
	);
};

const ProcessingPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Box sx={{ textAlign: 'center' }}>
				<CircularProgress sx={{ marginBottom: 2 }} />
				<Typography>{text.processingText}</Typography>
			</Box>
		</WelcomeScreen>
	);
};

const getDisplayName = (type) => {
	return CERTIFICATE_DISPLAY_NAMES[type] || type;
};

// Helper function to convert array of requirement groups to formatted display string
// Supports both flat arrays (backwards compatible) and nested arrays (OR relationships)
// Examples:
// - ['vca-basis', 'type-a'] → 'VCA Basis, type-a'
// - ['vca-basis', ['type-a', 'type-b']] → 'VCA Basis, type-a or type-b'
const formatTypesList = (requirementGroups, separator) => {
	return requirementGroups.map((group) => formatRequirementGroup(group, getDisplayName, separator)).join(', ');
};

const ConfirmationPage = ({
	text,
	userCertificates = [],
	userDocuments = [],
	onCertificateToggle,
	onDocumentChange,
	disabled,
	uploadUrl,
	input,
	useCamera,
	cameraIncludes,
	cameraRotationAngle,
	possibleDocumentTypes,
	requiredDocumentTypes,
	showTypesInSubtitle,
}) => {
	const hasCertificates = userCertificates.length > 0;

	const satisfiedTypes = getSatisfiedDocumentTypes('user', userCertificates, userDocuments);

	// Split allowed types into still-needed required and optional
	// Uses utility function that handles both flat arrays and nested arrays (OR relationships)
	const stillNeededRequired = getUnsatisfiedRequirements(requiredDocumentTypes, satisfiedTypes);
	// user safety certificate should
	// const optionalTypes = possibleDocumentTypes.filter(type =>
	// 	!requiredDocumentTypes.includes(type) && !satisfiedTypes.includes(type)
	// );

	const userInformation = hasCertificates
		? userCertificates.find((cert) => cert.firstName && cert.lastName && cert.selected === true)
		: undefined;
	const normalizeDate = (dateStr) => {
		if (!dateStr) return undefined;
		const date = new Date(dateStr);
		return date.toISOString(); // Returns format like "1990-09-19T22:00:00.000Z"
	};
	const essentialUserData = userInformation
		? {
				firstName: userInformation.firstName,
				lastName: userInformation.lastName,
				dateOfBirth: normalizeDate(userInformation.dateOfBirth),
			}
		: undefined;

	return (
		<ContentContainer>
			<Typography variant="h5" sx={{ marginBottom: 1 }}>
				{text.confirmationTitle}
			</Typography>

			{/* Found Certificates Section */}
			{hasCertificates && (
				<Box sx={{ marginBottom: 2 }}>
					<Typography variant="h6" sx={{ marginBottom: 1 }}>
						{text.foundCertificatesTitle}
					</Typography>
					<Typography variant="body2" sx={{ marginBottom: 2, color: 'text.secondary' }}>
						{text.foundCertificatesSubtitle}
					</Typography>
					{userCertificates.map((cert, index) => (
						<Card
							key={cert.certificateNumber || index}
							variant="outlined"
							sx={{ '& p': { margin: 0 }, marginBottom: 2, width: '100%' }}
						>
							<ListItem sx={{ height: 100 }} disabled={disabled}>
								<ListItemIcon sx={{ minWidth: 80 }}>
									<Checkbox
										checked={cert.selected !== false}
										onChange={(e) => onCertificateToggle(index, e.target.checked)}
										disabled={disabled}
									/>
								</ListItemIcon>
								<ListItemText
									primary={cert.certificateType}
									primaryTypographyProps={{ color: 'green', noWrap: true }}
									secondary={
										<Fragment>
											<Typography>
												{text.certificateNumber}: {cert.certificateNumber}
											</Typography>
											<Typography variant="caption">
												{text.certificateIssuer}: {cert.certificateIssuer}
											</Typography>
											{cert.validUntil && (
												<Typography variant="caption" sx={{ marginLeft: 1 }}>
													{text.validUntil}: {format(new Date(cert.validUntil), 'dd/MM/yyyy')}
												</Typography>
											)}
										</Fragment>
									}
								/>
							</ListItem>
						</Card>
					))}
				</Box>
			)}

			{/* No Certificates Found Message */}
			{!hasCertificates && (
				<Box sx={{ marginBottom: 2 }}>
					<Typography variant="h6" sx={{ marginBottom: 1 }}>
						{text.noCertificatesFoundTitle}
					</Typography>
					<Typography variant="body2" sx={{ marginBottom: 2, color: 'text.secondary' }}>
						{text.noCertificatesFoundMessage}
					</Typography>
				</Box>
			)}

			{/* Documents Upload Zone */}
			<Box sx={{ marginBottom: 4, width: '100%' }}>
				<Typography variant="h6" sx={{ marginBottom: 1 }}>
					{text.uploadDocumentsTitle}
				</Typography>
			<Typography variant="body2" sx={{ marginBottom: 1 }}>
				{stillNeededRequired.length > 0
					? showTypesInSubtitle
						? text.confirmationRequiredSubtitle + formatTypesList(stillNeededRequired, text.orSeparator)
						: text.confirmationRequiredSubtitle
					: text.confirmationRequiredSatisfied}
			</Typography>
			{possibleDocumentTypes.length > 0 && (
				<Typography variant="body2" sx={{ marginBottom: 2, color: 'text.secondary' }}>
					{showTypesInSubtitle
						? text.confirmationOptionalSubtitle + formatTypesList(possibleDocumentTypes, text.orSeparator)
						: text.confirmationOptionalSubtitle}
				</Typography>
			)}

				<MultipleFileUpload
					onFilesUploaded={onDocumentChange}
					uploadUrl={uploadUrl}
					disabled={disabled}
					text={text}
					existingFiles={userDocuments}
					fileType="unknown-user-document-type"
					allowedDocumentTypes={getTypesAcceptableForRequirements('user', [
						...flattenRequirementTypes(requiredDocumentTypes),
						...flattenRequirementTypes(possibleDocumentTypes),
					])}
					context={{
						expectedFile: 'safety or training related certificate of a person (not a company).',
						userData: { ...essentialUserData, ...input },
					}}
					possibleDocumentTypes={possibleDocumentTypes}
					requiredDocumentTypes={requiredDocumentTypes}
					getSatisfiedTypes={(certType) => getSatisfiedDocumentTypes('user', [{ type: certType, selected: true }], [])}
					useCamera={useCamera}
					cameraIncludes={cameraIncludes}
					cameraRotationAngle={cameraRotationAngle}
				/>
			</Box>
		</ContentContainer>
	);
};

const ThankYouPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography variant="h6" sx={{ marginBottom: 2 }}>
				{text.thankYouTitle}
			</Typography>
			<Typography variant="body1">{text.thankYouText}</Typography>
		</WelcomeScreen>
	);
};

// Main Component
const SafetyCertificatesUserSection = ({
	onChange = () => {},
	values = {},
	textOverride = {},
	background = 'light',
	navigationProps,
	selectedLanguage = 'EN',
	apiUrl = '/api/validate/certificates',
	uploadUrl = '/api/files',
	disabled = false,
	useCamera = true,
	cameraIncludes = '',
	cameraRotationAngle = 0,
	style = {},
	possibleDocumentTypes = [],
	requiredDocumentTypes = [],
	noWelcome = false,
	showTypesInSubtitle = true,
	...rest
}) => {
	const text = Object.assign({}, getTextDefaults(selectedLanguage), textOverride);

	const inputData = values.input || {}; // values entered by the user
	const userData = values.userData || {}; // values coming from refDataMap
	const input = {
		...userData,
		...inputData,
	};

	const userDocuments = values.userDocuments || [];

	// Local state: Store ALL certificates when fetched (for order and full list)
	// Selected certificates come from parent (values.userCertificates) - always up-to-date
	const [allCertificates, setAllCertificates] = useState([]);
	const selectedCertificates = values.userCertificates || [];

	// Helper to create a unique key for a certificate
	const getCertKey = (cert) => `${cert.certificateNumber}-${cert.certificateType}-${cert.certificateIssuer}`;

	// Create lookup for selected certificates
	const selectedByKey = {};
	selectedCertificates.forEach((cert) => {
		selectedByKey[getCertKey(cert)] = cert;
	});

	// Combine: iterate through all certificates, mark as selected if in parent
	// Deselected = allCertificates - selectedCertificates (computed, not stored)
	const userCertificates = allCertificates.map((cert) => ({
		...cert,
		selected: !!selectedByKey[getCertKey(cert)],
	}));

	// Check if user details are needed
	const needsUserDetails = !input.firstName || !input.lastName || !input.dateOfBirth;
	const defaultPage = noWelcome ? (needsUserDetails ? 'userDetails' : 'processing') : 'welcome';

	const handleCanContinue = () => {
		// Block if any uploads are invalid (AI rejected them)
		const hasInvalidUploads = userDocuments.some((doc) => doc.validity === false);
		if (hasInvalidUploads) return false;

		// Check if we have any valid content
		const hasSelectedCertificates = userCertificates.some((c) => c.selected !== false);
		const hasValidUploads = userDocuments.some((doc) => doc.validity !== false);
		const hasAnyContent = hasSelectedCertificates || hasValidUploads;

		// If no required types, just need any valid content
		if (requiredDocumentTypes.length === 0) {
			return hasAnyContent;
		}

		// Check if all required types are satisfied
		// getSatisfiedDocumentTypes already combines certificates + valid documents with hierarchy
		// isRequirementGroupSatisfied handles both flat arrays and nested arrays (OR relationships)
		const satisfiedTypes = getSatisfiedDocumentTypes('user', userCertificates, userDocuments);
		const allRequiredSatisfied = requiredDocumentTypes.every((requirementGroup) =>
			isRequirementGroupSatisfied(requirementGroup, satisfiedTypes),
		);

		return allRequiredSatisfied;
	};

	const screens = {
		welcome: {
			next: () => (needsUserDetails ? setCurrentScreen('userDetails') : handleBackgroundCheck()),
			component: WelcomePage,
			canContinue: true,
		},
		userDetails: {
			previous: (event) => {
				if (noWelcome) return navigationProps.previous(event);
				return setCurrentScreen('welcome');
			},
			next: () => handleBackgroundCheck(),
			component: UserDetailsPage,
			canContinue: !!(input.firstName && input.lastName && input.dateOfBirth),
		},
		processing: {
			component: ProcessingPage,
		},
		confirmation: {
			previous: () => {
				// Allow user to go back and correct their details (e.g. date of birth).
				// Returning to welcome would allow the flow to skip userDetails when input is filled,
				// making it impossible to correct typos.
				setCurrentScreen('userDetails');
			},
			next: () => setCurrentScreen('thankYou'),
			component: ConfirmationPage,
			canContinue: handleCanContinue(),
		},
		thankYou: {
			previous: () => setCurrentScreen('confirmation'),
			next: (event) => navigationProps.next(event),
			component: ThankYouPage,
			canContinue: true,
		},
	};

	const handleBackgroundCheck = async () => {
		try {
			setCurrentScreen('processing');

			// Use the same validation logic as VCASection
			const url = `${apiUrl}/user/${input.lastName}/${format(parseISO(input.dateOfBirth), 'yyyy-MM-dd')}`;

			const { body } = await request.get(url);

			if (!body || !body.length) {
				throw new Error('No certs');
			}

			// Filter certificates using the same logic as VCASection
			const certs = body.filter((c) => {
				const distanceFirstName = levenshtein(normalizeText(c.firstName), normalizeText(input.firstName));
				if (distanceFirstName < 2) return true;
			});

			if (!certs.length) {
				throw new Error('No certs');
			}

			// Store all certificates (for order and full list)
			setAllCertificates(certs);

			// Send all found certificates as selected to parent
			onChange({
				target: {
					name: 'userCertificates',
					value: certs,
				},
			});

			// Navigate to confirmation page (which now shows both certificates and upload)
			setCurrentScreen('confirmation');
		} catch (error) {
			console.error('Error searching for certificates:', error);
			// Even if no certificates found, go to confirmation page
			setAllCertificates([]);
			onChange({
				target: {
					name: 'userCertificates',
					value: [],
				},
			});
			setCurrentScreen('confirmation');
		}
	};

	const handleCertificateToggle = (index, selected) => {
		const cert = userCertificates[index];

		if (selected) {
			// Moving from deselected to selected: add to parent
			onChange({
				target: {
					name: 'userCertificates',
					value: [...selectedCertificates, cert],
				},
			});
		} else {
			// Moving from selected to deselected: remove from parent
			// Certificate stays in allCertificates, just not in selectedCertificates
			onChange({
				target: {
					name: 'userCertificates',
					value: selectedCertificates.filter((sc) => getCertKey(sc) !== getCertKey(cert)),
				},
			});
		}
	};

	const handleDocumentChange = (newFiles) => {
		onChange({
			target: {
				name: 'userDocuments',
				value: newFiles,
			},
		});
	};

	const handleInput = ({ target }) => onChange({ target: { ...target, name: target.name } });

	const [currentScreen, setCurrentScreen] = useState(defaultPage);
	const screen = screens[currentScreen] || screens.welcome;

	useEffect(() => {
		// If we should skip welcome and we have all user details, trigger background check
		if (noWelcome && !needsUserDetails && currentScreen === 'processing') {
			handleBackgroundCheck();
		}
	}, [noWelcome, needsUserDetails]);

	const screenProps = {
		input,
		userCertificates,
		userDocuments,
		onInput: handleInput,
		onCertificateToggle: handleCertificateToggle,
		onDocumentChange: handleDocumentChange,
		text,
		uploadUrl,
		disabled,
		useCamera,
		cameraIncludes,
		cameraRotationAngle,
		allowedDocumentTypes: [...requiredDocumentTypes, ...possibleDocumentTypes],
		possibleDocumentTypes,
		requiredDocumentTypes,
		showTypesInSubtitle,
		disableFirstName: !!userData.firstName,
		disableLastName: !!userData.lastName,
		disableDateOfBirth: !!userData.dateOfBirth,
	};

	return (
		<SectionContainer
			background={background}
			style={style}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={!!screen.previous}
					hasNext={currentScreen !== 'thankYou' || navigationProps.hasNext}
					canContinue={!!screen.canContinue}
					next={screen.next}
					previous={screen.previous}
					selectedLanguage={selectedLanguage}
				/>
			}
		>
			{screen && <screen.component {...screenProps} />}
		</SectionContainer>
	);
};

SafetyCertificatesUserSection.type = 'SafetyCertificatesUserSection';
SafetyCertificatesUserSection.hasNavigation = true;
SafetyCertificatesUserSection.spec = [
	{
		name: 'SafetyCertificatesUserSection',
		key: 'SafetyCertificatesUserSection',
		title: 'Section to collect user safety certificates',
		allowedFields: [],
		fields: [
		{
			type: 'TextField',
			name: 'title',
			label: 'Section title',
		},
		{
			type: 'Checkbox',
			name: 'showTypesInSubtitle',
			label: 'Show document types in subtitle?',
		},
		{
			type: 'Checkbox',
			name: 'useCamera',
			label: 'Force camera only (kiosk environments only — requires device to be registered)',
		},
	],
	defaultProperties: {
	refDataMap: {
			'userData.firstName': 'user.firstName',
			'userData.lastName': 'user.lastName',
			'userData.dateOfBirth': 'user.userData.dateOfBirth',
		},
			title: 'Safety Certificates User Section',
			apiUrl: '/api/validate/vca',
			showTypesInSubtitle: true,
		},
	},
];

export default SafetyCertificatesUserSection;
